import React, { useEffect, useRef } from 'react';

import { Box, Button } from '@material-ui/core';
import { useHistory, useLocation } from 'react-router-dom';
import { ReservationDTO } from 'api/controllers/reservations';
import i18n from 'i18n-js';

import { TableSkeleton } from 'components/shared';
import {
    formatAssignedTables,
    formatGuestName,
    formatGuestsCount,
    formatReservationDate,
    getReservationFilterKey,
    isReservationFilterActive,
    isRequestOverdue,
} from 'helpers/reservationHelper';
import { useAppSelector } from 'index';
import globalStore from 'store/globalStore';
import reservationsStore from 'store/reservationsStore';
import {
    ReservationsTabFilter,
    useAppliedReservationFilter,
    useReservationMaps,
    useRestaurantWallClock,
    useThunkDispatch,
} from '../hooks';
import { useFirstLoad } from 'hooks/useFirstLoad';
import ReservationDetailsPanel, { ReservationStatusChip } from './ReservationDetailsPanel';
import ReservationDaySummary from './ReservationDaySummary';
import ReservationsFilterBar from './ReservationsFilterBar';
import ReservationsTable, { ReservationColumn } from './ReservationsTable';
import ReservationActions from './ReservationActions';
import RequestActions from './RequestActions';

interface ActiveTabProps {
    filter: ReservationsTabFilter;
    onFilterChange: (filter: ReservationsTabFilter) => void;
    pinnedDay: string | null;
    onPinChange: (day: string | null) => void;
}

export default function ActiveTab({
    filter,
    onFilterChange,
    pinnedDay,
    onPinChange,
}: ActiveTabProps) {
    const history = useHistory();
    const location = useLocation<{ reservation?: ReservationDTO } | undefined>();
    const dispatch = useThunkDispatch();
    const {
        active,
        selectedActiveId,
        activeDetailsOverride,
        cancellingId,
        cancelDialogId,
        changeRevision,
    } = useAppSelector((state) => state.reservations);
    const { tables } = useReservationMaps();
    const { applied, isDebouncing } = useAppliedReservationFilter(filter);
    const appliedKey = getReservationFilterKey(applied);
    const isFirstLoad = useRef(true);
    const shownFilter = { day: active.day ?? null, search: active.search };
    const isShownFilterApplied =
        !active.isLoading &&
        active.day !== undefined &&
        getReservationFilterKey(shownFilter) === appliedKey;
    const wallClock = useRestaurantWallClock();
    const reservations = active.reservations.filter(
        (reservation) =>
            !reservation.isArchived &&
            (reservation.status !== 'Requested' || !isRequestOverdue(reservation, wallClock))
    );
    const selectedCandidate =
        reservations.find((reservation) => reservation.id === selectedActiveId) ||
        (activeDetailsOverride?.id === selectedActiveId ? activeDetailsOverride : undefined) ||
        (active.isLoading && location.state?.reservation?.id === selectedActiveId
            ? location.state.reservation
            : undefined);
    const selected =
        selectedCandidate?.status === 'Requested' &&
        (selectedCandidate.isArchived || isRequestOverdue(selectedCandidate, wallClock))
            ? undefined
            : selectedCandidate;
    const previousRealtimeSelection = useRef({ changeRevision, selected });

    const [isFirstLoading, settle] = useFirstLoad();

    useEffect(() => {
        dispatch(
            reservationsStore.actionCreators.getActiveReservations(applied.day, applied.search, {
                keepPrevious: !isFirstLoad.current,
                isList: true,
            })
        ).finally(settle);
        isFirstLoad.current = false;
    }, [appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isShownFilterApplied && selectedActiveId && !selected) {
            dispatch(reservationsStore.actionCreators.selectActive(null));
        }
    }, [isShownFilterApplied, selectedActiveId, !!selected]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const previous = previousRealtimeSelection.current;
        if (
            previous.changeRevision !== changeRevision &&
            previous.selected?.id === selected?.id &&
            previous.selected !== selected &&
            (previous.selected?.version !== selected?.version ||
                previous.selected?.status !== selected?.status) &&
            selected &&
            cancelDialogId === selected.id &&
            cancellingId !== selected.id
        ) {
            dispatch(
                globalStore.actionCreators.showToaster(
                    'error',
                    i18n.t('reservations.changedOnAnotherDevice')
                )
            );
        }
        previousRealtimeSelection.current = { changeRevision, selected };
    }, [changeRevision, selected, cancelDialogId, cancellingId, dispatch]);

    const columns: ReservationColumn[] = [
        {
            id: 'dateTime',
            label: i18n.t('reservations.columns.dateTime'),
            getText: (reservation) =>
                formatReservationDate(reservation.reservationStartDateTime, 'dd.MM.yy · HH:mm'),
            getSortValue: (reservation) => reservation.reservationStartDateTime,
        },
        { id: 'name', label: i18n.t('reservations.columns.name'), getText: formatGuestName },
        {
            id: 'guests',
            label: i18n.t('reservations.columns.guests'),
            getText: formatGuestsCount,
            getSortValue: (reservation) => reservation.guestsNumber,
        },
        {
            id: 'table',
            label: i18n.t('reservations.columns.table'),
            getText: (reservation) =>
                reservation.status === 'Requested'
                    ? '\u2014'
                    : formatAssignedTables(reservation.tables, tables),
        },
        {
            id: 'channel',
            label: i18n.t('reservations.columns.channel'),
            getText: (reservation) => i18n.t(`reservations.channels.${reservation.channel}`),
        },
        {
            id: 'status',
            label: i18n.t('reservations.columns.status'),
            getText: (reservation) =>
                i18n.t(`reservations.status.${reservation.status.toLowerCase()}`),
            render: (reservation) => <ReservationStatusChip status={reservation.status} />,
        },
    ];

    return (
        <Box display="flex" flexDirection="column" flex={1} minHeight={0} pl={3}>
            <ReservationsFilterBar
                value={filter}
                onChange={onFilterChange}
                isLoading={isDebouncing || active.isLoading}
                summary={filter.day ? <ReservationDaySummary day={filter.day} /> : null}
                pinnedDay={pinnedDay}
                onPinChange={onPinChange}
            />
            <Box position="relative" display="flex" flex={1} minHeight={0}>
                <ReservationsTable
                    columns={columns}
                    reservations={reservations}
                    selectedId={selectedActiveId}
                    onSelect={(id) => dispatch(reservationsStore.actionCreators.selectActive(id))}
                    emptyText={
                        isReservationFilterActive(shownFilter)
                            ? i18n.t('reservations.filters.noResults')
                            : undefined
                    }
                    isLoading={active.isLoading}
                    pageResetKey={appliedKey}
                />
                {selected && (
                    <ReservationDetailsPanel
                        key={selected.id}
                        reservation={selected}
                        tables={tables}
                        onClose={() =>
                            dispatch(reservationsStore.actionCreators.selectActive(null))
                        }
                        footer={
                            ((selected.status === 'Confirmed' || selected.status === 'Seated') &&
                                !selected.isArchived) ||
                            cancellingId === selected.id ||
                            cancelDialogId === selected.id ? (
                                <>
                                    <ReservationActions
                                        reservation={selected}
                                        tables={tables}
                                        onCancellationDialogChange={(isOpen) =>
                                            dispatch({
                                                type: 'RESERVATION_CANCEL_DIALOG',
                                                id: isOpen ? selected.id : null,
                                            })
                                        }
                                    />
                                    {(selected.status === 'Confirmed' ||
                                        selected.status === 'Seated') &&
                                        !selected.isArchived && (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                disabled={cancellingId === selected.id}
                                                onClick={() =>
                                                    history.push(
                                                        `/reservations/${selected.id}/edit`
                                                    )
                                                }
                                            >
                                                {i18n.t('reservations.edit')}
                                            </Button>
                                        )}
                                </>
                            ) : selected.status === 'Requested' && !selected.isArchived ? (
                                <RequestActions
                                    key={selected.id}
                                    reservation={selected}
                                    activeFilter={filter}
                                />
                            ) : undefined
                        }
                    />
                )}
                <TableSkeleton
                    isLoading={isFirstLoading}
                    columnWidths={[140, undefined, 80, 120, 120, 120]}
                />
            </Box>
        </Box>
    );
}
